import type {ObservableValue} from '@github-ui/observable'
import {createContext, type PropsWithChildren, type RefObject, use, useCallback} from 'react'

import type {TraceState} from './utilities/trace-reducer'

const ObservableTraceStateContext = createContext<RefObject<ObservableValue<TraceState> | null> | null>(null)

export function ObservableTraceStateProvider({
  children,
  value,
}: PropsWithChildren<{value: RefObject<ObservableValue<TraceState> | null> | null}>) {
  return <ObservableTraceStateContext value={value}>{children}</ObservableTraceStateContext>
}

/**
 * Returns a constant reference to a function that can be used inside a callback to get the current version of the trace state.
 *
 * The returned `getTraceState()` function should only be used inside an event handler or callback. If it is used in a render
 * method, it may return outdated state.
 */
export function useGetTraceState(): () => TraceState {
  const o = use(ObservableTraceStateContext)
  if (!o || !o.current) throw new Error('useGetTraceState can only be used inside a TraceProvider')
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  return useCallback(() => o.current!.value, [o])
}
